/**
 * Theme bootstrap (issue #424).
 *
 * Applies the user's theme to `<html data-theme>` per styling-spec.md. The
 * resolved theme is applied as early as possible (called from main.tsx before
 * React renders) using the localStorage mirror, so there is no light/dark flash
 * on reload before server-side preferences load. The slice re-applies once the
 * authoritative server value arrives.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

/** localStorage key for the interim theme mirror (per styling-spec). */
export const THEME_STORAGE_KEY = 'theme';

const VALID_THEMES: readonly ThemeMode[] = ['light', 'dark', 'system'];

function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (VALID_THEMES as readonly string[]).includes(value);
}

/** True when the OS currently prefers a dark color scheme. */
export function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Resolve a theme mode to the concrete `light`/`dark` actually applied. */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return systemPrefersDark() ? 'dark' : 'light';
  }
  return mode;
}

/**
 * Apply `mode` to `<html data-theme>` and mirror it to localStorage. `system`
 * resolves to the OS preference at apply time.
 */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(mode);
  document.documentElement.dataset.theme = resolved;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // localStorage may be unavailable (private mode / SSR); theme still applied.
  }
}

/** Read the persisted theme mode from localStorage, defaulting to `system`. */
export function readStoredTheme(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeMode(raw)) return raw;
  } catch {
    // ignore
  }
  return 'system';
}

/**
 * Apply the stored (or default) theme before first paint. Safe to call at
 * module load from main.tsx.
 */
export function bootstrapTheme(): ThemeMode {
  const mode = readStoredTheme();
  applyTheme(mode);
  return mode;
}
